"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryState, parseAsString, parseAsStringLiteral } from "nuqs";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  MoreHorizontal,
  Plus,
  PanelRightOpen,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@ocr-monorepo/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ocr-monorepo/ui/components/dropdown-menu";
import { cn } from "@ocr-monorepo/ui/lib/utils";
import UploadManager, { type UploadManagerHandle } from "@/components/upload-manager";
import { PdfViewer } from "@/components/pdf-view";
import { useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";
import { toast } from "sonner";
import { publicErrorMessage } from "@ocr-monorepo/shared";
import { CreateConverterDialog } from "./create-converter-dialog";
import { strToU8, zipSync } from "fflate";
import { ScrollArea } from "@ocr-monorepo/ui/components/scroll-area";
import {
  CONVERTER_DEMO_STORAGE_KEY,
  DEMO_ENVIRONMENT_RESET_EVENT,
  type ConverterDemoStatus,
} from "@/lib/demo-environment";
import { uploadAiSample } from "@/lib/upload-ai-sample";

const views = ["results", "files"] as const;

function serializeCsv(rows: Record<string, unknown>[]) {
  if (rows.length === 0) return "";
  const excluded = new Set([
    "created_at",
    "updated_at",
    "document_record_id",
  ]);
  const columns = Object.keys(rows[0]).filter((column) => !excluded.has(column));
  const escape = (value: unknown) => {
    const text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
  };
  return [columns.join(","), ...rows.map((row) => columns.map((column) => escape(row[column])).join(","))].join("\r\n");
}

function downloadCsvBundle(
  datasets: Array<{ name: string; rows: Record<string, unknown>[] }>,
  name: string,
) {
  const populated = datasets.filter((dataset) => dataset.rows.length > 0);
  if (populated.length === 0) return;
  const baseName = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "conversion";
  const isBundle = populated.length > 1;
  const body = isBundle
    ? zipSync(Object.fromEntries(populated.map((dataset) => [`${dataset.name}.csv`, strToU8(serializeCsv(dataset.rows))])))
    : serializeCsv(populated[0].rows);
  const url = URL.createObjectURL(new Blob([body], { type: isBundle ? "application/zip" : "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${baseName}.${isBundle ? "zip" : "csv"}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

const sampleRows = [
  ["Acme Corp", "INV-1001", "2024-01-15", "$3,875.00", "$325.00", "$4,200.00", "2024-02-15", "Paid"],
  ["Globex Ltd", "INV-1002", "2024-01-18", "$1,735.50", "$140.00", "$1,875.50", "2024-02-18", "Due"],
  ["Initech", "INV-1003", "2024-01-20", "$8,650.00", "$690.00", "$9,340.00", "2024-02-20", "Paid"],
  ["Umbrella Inc", "INV-1004", "2024-01-22", "$575.00", "$45.00", "$620.00", "2024-02-22", "Due"],
  ["Stark Industries", "INV-1005", "2024-01-25", "$13,700.00", "$1,100.00", "$14,800.00", "2024-02-25", "Paid"],
  ["Wayne Enterprises", "INV-1006", "2024-01-28", "$6,125.00", "$490.00", "$6,615.00", "2024-02-28", "Review"],
  ["Wonka Industries", "INV-1007", "2024-02-01", "$2,460.00", "$196.80", "$2,656.80", "2024-03-01", "Due"],
  ["Cyberdyne Systems", "INV-1008", "2024-02-04", "$11,250.00", "$900.00", "$12,150.00", "2024-03-04", "Paid"],
] as const;

const sampleFiles = [
  { name: "acme-consulting-jan.pdf", status: "Completed", pages: 3, size: "248 KB" },
  { name: "globex-hosting-jan.pdf", status: "Completed", pages: 2, size: "184 KB" },
  { name: "initech-services.pdf", status: "Completed", pages: 4, size: "316 KB" },
  { name: "umbrella-supplies.pdf", status: "Completed", pages: 1, size: "126 KB" },
  { name: "stark-manufacturing.pdf", status: "Completed", pages: 5, size: "492 KB" },
  { name: "wayne-enterprises.pdf", status: "Review", pages: 3, size: "271 KB" },
  { name: "wonka-packaging.pdf", status: "Processing", pages: 2, size: "198 KB" },
  { name: "cyberdyne-systems.pdf", status: "Queued", pages: 6, size: "584 KB" },
] as const;

function DocumentPreview({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-muted/20">
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-border px-4">
        <span className="text-xs font-medium">acme-consulting-jan.pdf</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">1 / 8</span>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close document preview">
            <X className="size-4" />
          </Button>
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1" scrollbarOrientation="both">
        <div className="p-6">
        <div className="mx-auto min-h-[520px] max-w-sm border border-border bg-background p-9 shadow-sm">
          <div className="flex items-start justify-between border-b border-border pb-6">
            <div>
              <p className="text-base font-bold tracking-tight">ACME CORP</p>
              <p className="mt-1 text-[9px] text-muted-foreground">415 Market Street · San Francisco, CA</p>
            </div>
            <FileText className="size-7 text-primary" />
          </div>
          <div className="mt-7 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-muted-foreground">Bill to</p>
              <p className="mt-1 text-[10px] font-medium">PdfParse Operations</p>
              <p className="text-[9px] leading-4 text-muted-foreground">21 King Street<br />Kingston, Jamaica</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-muted-foreground">Invoice</p>
              <p className="mt-1 text-[10px] font-semibold">INV-1001</p>
              <p className="mt-1 text-[9px] text-muted-foreground">Jan 15, 2024</p>
            </div>
          </div>
          <div className="mt-8 border-y border-border py-3 text-[9px]">
            <div className="grid grid-cols-[1fr_auto] gap-4 font-mono text-[8px] uppercase tracking-[0.1em] text-muted-foreground">
              <span>Description</span><span>Amount</span>
            </div>
            <div className="mt-3 grid grid-cols-[1fr_auto] gap-4"><span>Document processing platform</span><span>$2,400.00</span></div>
            <div className="mt-2 grid grid-cols-[1fr_auto] gap-4"><span>API usage — January</span><span>$1,275.00</span></div>
            <div className="mt-2 grid grid-cols-[1fr_auto] gap-4"><span>Priority support</span><span>$200.00</span></div>
          </div>
          <div className="ml-auto mt-6 w-40 space-y-2 text-[9px]">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>$3,875.00</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Tax</span><span>$325.00</span></div>
            <div className="flex justify-between border-t border-border pt-2 text-[11px] font-semibold"><span>Total</span><span>$4,200.00</span></div>
            <div className="flex justify-between text-primary"><span>Due</span><span>Feb 15, 2024</span></div>
          </div>
          <div className="mt-8 border-t border-border pt-4">
            <p className="text-[8px] leading-4 text-muted-foreground">Thank you for your business. Payment terms: Net 30.</p>
          </div>
        </div>
        </div>
      </ScrollArea>
    </div>
  );
}

function ResultsView({ previewOpen, onPreviewOpenChange }: { previewOpen: boolean; onPreviewOpenChange: (open: boolean) => void }) {
  return (
    <div className={cn("grid min-h-0 flex-1 transition-[grid-template-columns] duration-300 ease-out md:grid-cols-[minmax(0,1fr)_minmax(0,0fr)]", previewOpen && "md:grid-cols-[minmax(480px,1.25fr)_minmax(320px,0.75fr)]")}>
      <ScrollArea className={cn("min-h-0 bg-background transition-[border-color] duration-300 ease-out", previewOpen && "md:border-r md:border-border")} scrollbarOrientation="both">
        <div className="flex h-12 min-w-[1050px] items-center gap-6 border-b border-border px-4 text-[10px] text-muted-foreground">
          <span><strong className="mr-1 text-foreground">8</strong> rows extracted</span>
          <span><strong className="mr-1 text-foreground">8</strong> documents</span>
          <span><strong className="mr-1 text-foreground">$51,657.30</strong> total value</span>
          <span className="ml-auto font-mono uppercase tracking-[0.1em]">8 columns · CSV ready</span>
        </div>
        <table className="w-full min-w-[1050px] table-fixed border-collapse text-left text-xs">
          <thead className="sticky top-0 z-10 bg-muted/75 backdrop-blur">
            <tr className="text-muted-foreground">
              {['Vendor', 'Invoice #', 'Invoice date', 'Subtotal', 'Tax', 'Total', 'Due date', 'Status'].map((heading) => (
                <th key={heading} className="overflow-hidden whitespace-nowrap border-r border-b border-border px-3 py-1.5 font-mono text-[10px] font-medium text-ellipsis last:border-r-0">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sampleRows.map((row, rowIndex) => (
              <tr key={row[1]} className="hover:bg-muted/25">
                {row.map((cell, cellIndex) => (
                  <td key={cell} className="overflow-hidden whitespace-nowrap border-r border-b border-border px-3 py-2.5 text-ellipsis last:border-r-0">
                    {cellIndex === 0 && <span className="mr-3 font-mono text-[9px] text-muted-foreground">{rowIndex + 1}</span>}
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
      <div className={cn("invisible min-h-0 overflow-hidden opacity-0 transition-opacity duration-200 ease-out", previewOpen && "visible opacity-100 delay-100")}>
        <DocumentPreview onClose={() => onPreviewOpenChange(false)} />
      </div>
    </div>
  );
}

function FilesView() {
  return (
    <ScrollArea className="min-h-0 flex-1 bg-background" scrollbarOrientation="vertical">
      <div className="mx-auto max-w-5xl p-6 lg:p-8">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Uploaded files</h2>
            <p className="mt-1 text-sm text-muted-foreground">Review source documents and extraction status.</p>
          </div>
          <Button size="sm"><Upload className="mr-2 size-4" />Upload files</Button>
        </div>
        <div className="overflow-hidden rounded-lg border border-border">
          {sampleFiles.map((file, index) => {
            const isComplete = file.status === "Completed";
            const isProcessing = file.status === "Processing";
            const isReview = file.status === "Review";
            return (
            <div key={file.name} className="flex items-center gap-4 border-b border-border bg-card px-4 py-3.5 last:border-b-0 hover:bg-muted/25">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-primary"><FileText className="size-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{file.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{file.pages} pages · {file.size}</p>
              </div>
              <span className={cn("hidden items-center gap-1.5 text-xs sm:flex", isComplete && "text-emerald-700 dark:text-emerald-400", isProcessing && "text-blue-700 dark:text-blue-400", isReview && "text-amber-700 dark:text-amber-400", !isComplete && !isProcessing && !isReview && "text-muted-foreground")}>
                {isComplete ? <CheckCircle2 className="size-3.5" /> : isProcessing ? <Loader2 className="size-3.5 animate-spin" /> : isReview ? <AlertCircle className="size-3.5" /> : <span className="size-1.5 rounded-full bg-current" />}
                {file.status}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon-sm" aria-label={`Actions for ${file.name}`}><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>View document</DropdownMenuItem>
                  <DropdownMenuItem>Download original</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive focus:text-destructive">Remove from conversion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="w-5 text-right font-mono text-[10px] text-muted-foreground">{index + 1}</span>
            </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}

export function PdfToCsvWorkspace({ preview = false, demo = false }: { preview?: boolean; demo?: boolean }) {
  const [view, setView] = useQueryState("view", parseAsStringLiteral(views).withDefault("results"));
  const [documentPreview, setDocumentPreview] = useQueryState(
    "documentPreview",
    parseAsStringLiteral(["open", "closed"] as const).withDefault("open"),
  );
  const [dataset, setDataset] = useQueryState(
    "dataset",
    parseAsString.withDefault(""),
  );
  const [selectedDocument, setSelectedDocument] = useQueryState(
    "document",
    parseAsString.withDefault(""),
  );
  const previewOpen = documentPreview === "open";
  const searchParams = useSearchParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const uploadRef = useRef<UploadManagerHandle>(null);
  const demoTimerRef = useRef<number | null>(null);
  const [demoStatus, setDemoStatus] = useState<ConverterDemoStatus>("ready");
  const [demoFileNames, setDemoFileNames] = useState<string[]>([]);
  const { data: session } = useSession();
  const conversionId = searchParams.get("conversion");
  const conversionQuery = useQuery({
    ...trpc.converters.get.queryOptions({ id: conversionId ?? "" }),
    enabled: !preview && !!conversionId,
    refetchInterval: (query) => ["queued", "pending", "waiting", "in_progress", "processing"].includes(query.state.data?.jobStatus ?? "") ? 3_000 : 30_000,
  });
  const lastPreviewedJobRef = useRef<string | null>(null);
  const datasetsQuery = useQuery({
    ...trpc.converters.datasets.queryOptions({ conversionId: conversionId ?? "" }),
    enabled: !preview && !!conversionId,
    refetchInterval: ["queued", "pending", "waiting", "in_progress", "processing"].includes(conversionQuery.data?.jobStatus ?? "") ? 3_000 : false,
  });
  const attachFiles = useMutation(trpc.converters.attachFiles.mutationOptions());
  const startJob = useMutation(trpc.jobs.create.mutationOptions());
  const configureSchema = useMutation(
    trpc.converters.configureSchema.mutationOptions(),
  );

  useEffect(() => {
    if (!demo) return;
    const loadStatus = () => {
      const stored = window.localStorage.getItem(CONVERTER_DEMO_STORAGE_KEY);
      setDemoStatus(stored === "empty" ? "empty" : "ready");
    };
    loadStatus();
    window.addEventListener(DEMO_ENVIRONMENT_RESET_EVENT, loadStatus);
    return () => {
      window.removeEventListener(DEMO_ENVIRONMENT_RESET_EVENT, loadStatus);
      if (demoTimerRef.current) window.clearTimeout(demoTimerRef.current);
    };
  }, [demo]);

  const runDemoUpload = useCallback((files: File[]) => {
    if (!demo) return;
    setDemoFileNames(files.map((file) => file.name));
    setDemoStatus("processing");
    window.localStorage.setItem(CONVERTER_DEMO_STORAGE_KEY, "processing");
    toast.loading("Inferring the best CSV schema…", { id: "demo-converter" });
    demoTimerRef.current = window.setTimeout(() => {
      setDemoStatus("ready");
      window.localStorage.setItem(CONVERTER_DEMO_STORAGE_KEY, "ready");
      toast.success("CSV schema ready", {
        id: "demo-converter",
        description: `${files.length} PDF${files.length === 1 ? " was" : "s were"} extracted into clean rows.`,
      });
    }, 1600);
  }, [demo]);

  useEffect(() => {
    const completedJobId =
      conversionQuery.data?.jobStatus === "completed"
        ? conversionQuery.data.conversion.job_id
        : null;
    if (!completedJobId || lastPreviewedJobRef.current === completedJobId) {
      return;
    }
    lastPreviewedJobRef.current = completedJobId;
    void setDocumentPreview("open");
  }, [conversionQuery.data, setDocumentPreview]);

  const prepareFiles = useCallback(async (filesToPrepare: File[]) => {
    if (!conversionId || !conversionQuery.data) return;
    if (
      conversionQuery.data.files.length > 0 ||
      !conversionQuery.data.workspace.tableName.startsWith("converter_staging_")
    ) {
      return conversionQuery.data.workspace;
    }
    const sample = filesToPrepare[0];
    toast.loading("Inferring the best CSV schema…", { id: "infer-csv-schema" });
    try {
      const configured = await configureSchema.mutateAsync({
        conversionId,
        filename: sample.name,
        sampleId: await uploadAiSample(sample, "converter-schema"),
      });
      await conversionQuery.refetch();
      toast.success("CSV schema ready", {
        id: "infer-csv-schema",
        description:
          configured.schema.childTables.length > 0
            ? `Created ${configured.schema.parentTable} with ${configured.schema.childTables.length} related table${configured.schema.childTables.length === 1 ? "" : "s"}.`
            : `Created ${configured.schema.parentTable}.`,
      });
      return {
        projectSlug: configured.projectSlug,
        tableSlug: configured.tableSlug,
      };
    } catch (error) {
      toast.error("Could not infer a CSV schema", {
        id: "infer-csv-schema",
        description:
          publicErrorMessage("system_error"),
      });
      throw error;
    }
  }, [configureSchema, conversionId, conversionQuery]);

  const handleUploadedFiles = useCallback(async (fileIds: string[]) => {
    if (!conversionId || !conversionQuery.data) return;
    try {
      const attachment = await attachFiles.mutateAsync({ conversionId, fileIds });
      if (attachment.rejected.length > 0) {
        const limitReached = attachment.rejected.some(
          (file) => file.reason === "file_limit",
        );
        toast.warning(
          `${attachment.rejected.length} PDF${attachment.rejected.length === 1 ? " was" : "s were"} not attached`,
          {
            description:
              limitReached
                ? "This temporary conversion has reached its upload limit. Upgrade to add more PDFs."
                : attachment.attachedFileIds.length > 0
                ? "The valid uploads will still be processed. The remaining upload exceeded a temporary conversion limit or did not finish."
                : "The uploads exceeded a temporary conversion limit or did not finish.",
          },
        );
      }
      if (attachment.attachedFileIds.length === 0) {
        await conversionQuery.refetch();
        return;
      }
      await startJob.mutateAsync({
        conversionId,
        projectSlug: conversionQuery.data.workspace.projectSlug,
        tableSlug: conversionQuery.data.workspace.tableSlug,
        documentKeys: attachment.attachedFileIds,
        processingPreference: "auto",
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.converters.get.queryKey({ id: conversionId }) }),
        queryClient.invalidateQueries({ queryKey: trpc.converters.datasets.queryKey({ conversionId }) }),
        queryClient.invalidateQueries({ queryKey: trpc.converters.list.queryKey({ type: "pdf_to_csv" }) }),
      ]);
      toast.success("Extraction started", { description: `${attachment.attachedFileIds.length} PDF${attachment.attachedFileIds.length === 1 ? "" : "s"} added to this conversion.` });
    } catch (error) {
      toast.error("Could not start extraction", { description: publicErrorMessage("file_processing_error") });
    }
  }, [attachFiles, conversionId, conversionQuery.data, queryClient, startJob, trpc]);

  if (!preview) {
    if (conversionId && conversionQuery.data) {
      const { conversion, workspace, files, jobStatus } = conversionQuery.data;
      const datasets = datasetsQuery.data ?? [];
      const activeDataset =
        datasets.find((item) => item.name === dataset) ??
        datasets[1] ??
        datasets[0];
      const rows = (activeDataset?.rows ?? []) as Record<string, unknown>[];
      const columns = rows.length > 0
        ? Object.keys(rows[0]).filter((column) => !["created_at", "updated_at", "document_record_id"].includes(column))
        : [];
      const selectedFile =
        files.find((file) => file.fileId === selectedDocument) ?? files[0];
      const selectedFileId = selectedFile?.fileId ?? undefined;
      const isAnonymous = Boolean(
        (session?.user as { isAnonymous?: boolean } | undefined)?.isAnonymous,
      );
      const openRowDocument = (row: Record<string, unknown>) => {
        const documentId = String(row.document_record_id ?? "");
        if (!documentId || !files.some((file) => file.fileId === documentId)) {
          return;
        }
        void Promise.all([
          setSelectedDocument(documentId),
          setDocumentPreview("open"),
        ]);
      };

      return (
        <div className="flex h-full min-h-0 flex-col bg-background">
          <header className="shrink-0 border-b border-border bg-card/80 backdrop-blur">
            <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 px-4 py-1.5 lg:px-5">
              <div className="min-w-0">
                <h1 className="truncate text-xs font-semibold">{conversion.name}</h1>
                <p className="text-[10px] text-muted-foreground">{files.length} file{files.length === 1 ? "" : "s"} · {jobStatus ?? conversion.status}</p>
              </div>
              <div className="flex items-center gap-2">
                {isAnonymous && conversionQuery.data.uploadAllowance && (
                  <div className="hidden items-center gap-2 border-r border-border pr-2 sm:flex">
                    <span className="font-mono text-[9px] text-muted-foreground">
                      {conversionQuery.data.uploadAllowance.pagesRemaining}/
                      {conversionQuery.data.uploadAllowance.pagesLimit} pages left
                    </span>
                    <Button asChild variant="ghost" size="sm">
                      <Link href="/getting-started/create-account?source=converter-upgrade">
                        Upgrade <ArrowUpRight className="ml-1 size-3" />
                      </Link>
                    </Button>
                  </div>
                )}
                {!previewOpen && view === "results" && (
                  <Button variant="ghost" size="sm" onClick={() => setDocumentPreview("open")}>
                    <PanelRightOpen className="mr-1.5 size-3" />Show document
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => uploadRef.current?.openFileDialog()} disabled={!session?.user.id}>
                  <Upload className="mr-1.5 size-3" />Upload PDFs
                </Button>
                <Button size="sm" disabled={!datasets.some((item) => item.rows.length > 0)} onClick={() => downloadCsvBundle(datasets, conversion.name)}>
                  <Download className="mr-1.5 size-3" />Download CSV
                </Button>
              </div>
            </div>
            <div className="flex h-10 items-end gap-5 px-4 lg:px-6">
              {views.map((item) => (
                <button key={item} type="button" onClick={() => setView(item)} className={cn("relative h-10 text-xs font-medium capitalize text-muted-foreground transition-colors hover:text-foreground", view === item && "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary")}>
                  {item}{item === "files" && <span className="ml-1.5 font-mono text-[9px]">{files.length}</span>}
                </button>
              ))}
            </div>
          </header>

          {files.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center p-6">
              <div className="max-w-md text-center">
                <span className="mx-auto flex size-12 items-center justify-center rounded-xl border border-border bg-card text-primary">
                  <Upload className="size-5" />
                </span>
                <h2 className="mt-5 text-xl font-semibold tracking-tight">Upload a PDF to begin</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  We’ll infer the document structure, create related CSV tables, and keep the source PDF beside the results.
                </p>
                <Button className="mt-6" onClick={() => uploadRef.current?.openFileDialog()} disabled={!session?.user.id}>
                  <Upload className="mr-2 size-4" />Upload PDF
                </Button>
              </div>
            </div>
          ) : view === "results" ? (
            <div className={cn("grid min-h-0 flex-1 transition-[grid-template-columns] duration-300 ease-out md:grid-cols-[minmax(0,1fr)_minmax(0,0fr)]", previewOpen && "md:grid-cols-[minmax(480px,1.25fr)_minmax(320px,0.75fr)]")}>
              <div className={cn("flex min-h-0 flex-col overflow-hidden bg-background transition-[border-color] duration-300 ease-out", previewOpen && "md:border-r md:border-border")}>
                {datasets.length > 0 && (
                  <ScrollArea className="h-10 shrink-0 border-b border-border" scrollbarOrientation="horizontal">
                    <div className="flex h-10 min-w-max items-end gap-5 px-4">
                    {datasets.map((item) => (
                      <Button
                        key={item.name}
                        variant="ghost"
                        size="sm"
                        onClick={() => setDataset(item.name)}
                        className={cn(
                          "relative h-10 shrink-0 rounded-none px-0 text-xs font-medium capitalize text-muted-foreground hover:bg-transparent hover:text-foreground",
                          activeDataset?.name === item.name &&
                            "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary",
                        )}
                      >
                        {item.name.replaceAll("_", " ")}
                        <span className="ml-1.5 font-mono text-[9px]">{item.rows.length}</span>
                      </Button>
                    ))}
                    </div>
                  </ScrollArea>
                )}
                <div className="flex h-9 shrink-0 items-center gap-6 border-b border-border px-3 text-[9px] text-muted-foreground">
                  <span><strong className="mr-1 text-foreground">{rows.length}</strong> rows extracted</span>
                  <span><strong className="mr-1 text-foreground">{files.length}</strong> documents</span>
                  <span className="ml-auto font-mono uppercase tracking-[0.08em]">{columns.length} columns · CSV {rows.length > 0 ? "ready" : "processing"}</span>
                </div>
                <ScrollArea className="min-h-0 flex-1" scrollbarOrientation="both">
                <table
                  className="table-fixed border-collapse text-left text-xs"
                  style={{ width: `${Math.max(columns.length * 172, 860)}px` }}
                >
                  <colgroup>
                    {columns.map((column) => <col key={column} style={{ width: "172px" }} />)}
                  </colgroup>
                  <thead className="sticky top-0 bg-muted/95 backdrop-blur-sm"><tr>{columns.map((column) => {
                    const label = column.replaceAll("_", " ");
                    return <th key={column} className="relative overflow-hidden whitespace-nowrap border-r border-b border-border px-3 py-1.5 font-mono text-[10px] font-medium text-muted-foreground" title={label}>
                      <span className="block overflow-hidden whitespace-nowrap pr-5">{label}</span>
                      <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-7 bg-gradient-to-r from-transparent to-muted" />
                    </th>;
                  })}</tr></thead>
                  <tbody>{rows.map((row, index) => <tr
                    key={String(row.id ?? index)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open source document for row ${index + 1}`}
                    className="cursor-pointer text-xs transition-colors hover:bg-muted/20 focus-visible:bg-muted/25 focus-visible:outline-none"
                    onClick={() => openRowDocument(row)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openRowDocument(row);
                      }
                    }}
                  >{columns.map((column) => {
                    const value = String(row[column] ?? "");
                    const isIdentifier = column === "id" || column.endsWith("_id");
                    return <td key={column} className={cn("overflow-hidden whitespace-nowrap border-r border-b border-border px-3 py-2.5 text-ellipsis", isIdentifier && "font-mono")} title={value}>{value}</td>;
                  })}</tr>)}</tbody>
                </table>
                {rows.length === 0 && <div className="p-8 text-sm text-muted-foreground">Extraction is running. Related tables will appear here automatically.</div>}
                </ScrollArea>
              </div>
              <div className={cn("invisible min-h-0 overflow-hidden bg-muted/20 opacity-0 transition-opacity duration-200 ease-out", previewOpen && "visible opacity-100 delay-100")}>
                <div className="flex h-full min-h-0 flex-col">
                  <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-3">
                    <div className="min-w-0">
                      <p className="font-mono text-[8px] uppercase tracking-[0.12em] text-muted-foreground">Source document</p>
                      <p className="truncate text-[11px] font-medium">{selectedFile?.name ?? "Source document"}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-[9px] text-muted-foreground">{selectedFile?.pageCount ?? "—"} pages</span>
                      <Button variant="ghost" size="icon-sm" onClick={() => setDocumentPreview("closed")} aria-label="Close document preview"><X className="size-3.5" /></Button>
                    </div>
                  </div>
                  <div className="min-h-0 flex-1"><PdfViewer id={selectedFileId} projectSlug={workspace.projectSlug} compact /></div>
                </div>
              </div>
            </div>
          ) : (
            <ScrollArea className="min-h-0 flex-1" scrollbarOrientation="vertical">
              <div className="mx-auto max-w-5xl space-y-2 p-6 lg:p-8">
                {files.map((file) => <div key={file.id} className="flex items-center gap-4 border-b border-border py-3"><FileText className="size-4 text-primary" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">{file.pageCount ?? "—"} pages · {file.status}</p></div></div>)}
                {files.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No files uploaded yet.</p>}
              </div>
            </ScrollArea>
          )}

          {session?.user.id && (
            <UploadManager
              ref={uploadRef}
              userId={session.user.id}
              projectSlug={workspace.projectSlug}
              tableSlug={workspace.tableSlug}
              uploadScope="current-table"
              showButton={false}
              embedded
              remainingFileSlots={conversionQuery.data.uploadAllowance?.filesRemaining}
              prepareFiles={prepareFiles}
              onUploadComplete={handleUploadedFiles}
            />
          )}
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col bg-background">
        <div className="flex h-14 items-center justify-between border-b border-border px-5 lg:px-6">
          <div><h1 className="text-sm font-semibold">Converters</h1><p className="text-xs text-muted-foreground">New conversion</p></div>
        </div>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-md text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-xl border border-border bg-card text-primary"><FileText className="size-5" /></span>
            <h2 className="mt-5 text-xl font-semibold tracking-tight">Convert PDFs into clean rows</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Upload invoices or bank statements. Extracted data will appear in the table beside the original document.</p>
            <CreateConverterDialog trigger={<Button className="mt-6"><Plus className="mr-2 size-4" />Start a conversion</Button>} />
          </div>
        </div>
      </div>
    );
  }

  if (demo && demoStatus !== "ready") {
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        <header className="shrink-0 border-b border-border bg-card/80 backdrop-blur">
          <div className="flex min-h-11 items-center justify-between px-4 py-1.5 lg:px-5">
            <div className="min-w-0">
              <h1 className="truncate text-xs font-semibold">January vendor invoices</h1>
              <p className="text-[10px] text-muted-foreground">
                {demoStatus === "processing" ? `${demoFileNames.length} file${demoFileNames.length === 1 ? "" : "s"} · processing` : "0 files · ready"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => uploadRef.current?.openFileDialog()} disabled={demoStatus === "processing"}>
              <Upload className="mr-1.5 size-3" />Upload PDFs
            </Button>
          </div>
          <div className="flex h-10 items-end gap-5 px-4 lg:px-6">
            <button type="button" className="relative h-10 text-xs font-medium text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary">results</button>
            <button type="button" className="h-10 text-xs font-medium text-muted-foreground">files<span className="ml-1.5 font-mono text-[9px]">{demoFileNames.length}</span></button>
          </div>
        </header>
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="max-w-md text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-md border border-border bg-card text-primary">
              {demoStatus === "processing" ? <Loader2 className="size-5 animate-spin" /> : <Upload className="size-5" />}
            </span>
            <h2 className="mt-5 text-xl font-semibold tracking-tight">
              {demoStatus === "processing" ? "Extracting uploaded PDFs" : "Upload a PDF to begin"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {demoStatus === "processing"
                ? `Inferring the CSV schema for ${demoFileNames.join(", ")} and creating the invoice dataset.`
                : "We’ll infer the document structure, create related CSV tables, and keep the source PDF beside the results."}
            </p>
            <Button className="mt-6" onClick={() => uploadRef.current?.openFileDialog()} disabled={demoStatus === "processing"}>
              {demoStatus === "processing" ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
              {demoStatus === "processing" ? "Processing…" : "Upload PDF"}
            </Button>
          </div>
        </div>
        <UploadManager
          ref={uploadRef}
          userId="converter-demo-user"
          projectSlug="converter-demo"
          tableSlug="invoices"
          uploadScope="current-table"
          showButton={false}
          embedded
          simulateFiles={runDemoUpload}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="shrink-0 border-b border-border bg-card/80 backdrop-blur">
        <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 px-4 py-1.5 lg:px-5">
          <div className="min-w-0"><h1 className="truncate text-xs font-semibold">January vendor invoices</h1><p className="text-[10px] text-muted-foreground">Updated just now · 8 files · $51,657.30</p></div>
          <div className="flex items-center gap-2">
            {!previewOpen && <Button variant="ghost" size="sm" onClick={() => setDocumentPreview("open")}><PanelRightOpen className="mr-1.5 size-3" />Show document</Button>}
            <Button variant="outline" size="sm" onClick={demo ? () => uploadRef.current?.openFileDialog() : undefined}><Upload className="mr-1.5 size-3" />Upload PDFs</Button>
            <Button size="sm" onClick={() => downloadCsvBundle([{ name: "invoices", rows: sampleRows.map((row, index) => ({ id: index + 1, vendor: row[0], invoice_number: row[1], invoice_date: row[2], subtotal: row[3], tax: row[4], total: row[5], due_date: row[6], status: row[7] })) }], "january-vendor-invoices")}><Download className="mr-1.5 size-3" />Download CSV</Button>
            <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon-sm" aria-label="Conversion actions"><MoreHorizontal className="size-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuItem>Rename</DropdownMenuItem><DropdownMenuItem>Move to folder</DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive focus:text-destructive">Delete conversion</DropdownMenuItem></DropdownMenuContent></DropdownMenu>
          </div>
        </div>
        <div className="flex h-10 items-end gap-5 px-4 lg:px-6">
          {views.map((item) => (
            <button key={item} type="button" onClick={() => setView(item)} className={cn("relative h-10 text-xs font-medium capitalize text-muted-foreground transition-colors hover:text-foreground", view === item && "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary")}>
              {item}{item === "files" && <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 font-mono text-[9px]">8</span>}
            </button>
          ))}
        </div>
      </header>
      {view === "results" ? <ResultsView previewOpen={previewOpen} onPreviewOpenChange={(open) => setDocumentPreview(open ? "open" : "closed")} /> : <FilesView />}
      {demo && (
        <UploadManager
          ref={uploadRef}
          userId="converter-demo-user"
          projectSlug="converter-demo"
          tableSlug="invoices"
          uploadScope="current-table"
          showButton={false}
          embedded
          simulateFiles={runDemoUpload}
        />
      )}
    </div>
  );
}
